import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { type ErrorNotification } from "@via/utils/interfaces";
import {
  type CreatePersonalAccessTokenRequestBody,
  type CreatePersonalAccessTokenResponse,
  type ListPersonalAccessTokensResponse,
  OrderBy,
} from "common";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";

export const fetchPersonalAccessTokens = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  userId?: number
): Promise<ListPersonalAccessTokensResponse> => {
  try {
    const baseUrl = "/api/auth/v1/personal-access-token";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? OrderBy.Desc : OrderBy.Asc);
    }

    if (filter && filter.length > 0) {
      const filterQuery = filter.map(f => `${f.id}:${f.value}`).join(";");
      queryParams.append("filter", filterQuery);
    }

    if (userId) {
      queryParams.append("userId", userId.toString());
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const usePersonalAccessTokens = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = []
): UseQueryResult<ListPersonalAccessTokensResponse, ErrorNotification> => {
  const skip = page * pageSize;
  const take = pageSize;
  const impersonationId = useImpersonationUser();

  return useQuery<ListPersonalAccessTokensResponse, ErrorNotification>({
    queryKey: [
      "/api/auth/v1/personal-access-token",
      take,
      skip,
      sort,
      impersonationId,
    ],
    queryFn: () =>
      fetchPersonalAccessTokens(take, skip, sort, undefined, impersonationId),
    retry: false,
  });
};

export const useCreatePersonalAccessToken = (): UseMutationResult<
  CreatePersonalAccessTokenResponse,
  ErrorNotification,
  CreatePersonalAccessTokenRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    CreatePersonalAccessTokenResponse,
    ErrorNotification,
    CreatePersonalAccessTokenRequestBody
  >({
    mutationFn: async (
      payload: CreatePersonalAccessTokenRequestBody
    ): Promise<CreatePersonalAccessTokenResponse> => {
      const res = await axios.post(
        "/api/auth/v1/personal-access-token",
        payload
      );
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "personal-access-token-created",
        variant: NOTIFICATION.SUCCESS,
        message: "Personal access token created successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/personal-access-token"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "personal-access-token-creation-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to create personal access token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};

export const useDeletePersonalAccessToken = (): UseMutationResult<
  void,
  ErrorNotification,
  number
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, ErrorNotification, number>({
    mutationFn: async (tokenId: number) => {
      const res = await axios.delete(
        `/api/auth/v1/personal-access-token/${tokenId}`
      );
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "personal-access-token-deleted",
        variant: NOTIFICATION.SUCCESS,
        message: "Personal access token deleted successfully",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/personal-access-token"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "personal-access-token-deletion-failed",
        variant: NOTIFICATION.ERROR,
        message: "Failed to delete personal access token",
        details: error.response?.data?.message,
      });
      throw error;
    },
  });
};
